import { selectReportCategoryQuery } from 'util/selector/querySelector';
import { selectReportOrCurrentProjectId } from 'util/selector/reportSelector';

import {
  selectDepartments,
  selectIsDepartmentIdSelected,
  selectSelectedDepartmentIds,
  selectSelectedDepartments,
} from 'reduxStore/departments/selectors';
import { departmentApi } from 'container/departmentApi';
import { departmentMapper } from 'container/departmentMapper';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { serializeError } from 'shared/utils/redux';
import { Department } from 'model/Department';
import { selectCanApplyFilter } from 'reduxStore/filtering/selectors';

import { STORE_NAME } from './initialState';
import { toggleDepartmentCollapsedState } from './slice';

export const fetchDepartments = createAsyncThunk<Department[]>(
  `${STORE_NAME}/fetchDepartments`,
  (_, { getState }) => {
    const reportCategory = selectReportCategoryQuery(getState());
    const projectId = selectReportOrCurrentProjectId(getState());

    return departmentApi
      .list(projectId, { reportCategory })
      .then((list) => list.map((department) => departmentMapper.deserialize(department)));
  },
  { serializeError }
);

export const toggleDepartment = createAsyncThunk(
  `${STORE_NAME}/toggleDepartment`,
  ({ departmentId }: { departmentId: number }, { getState }) => {
    const selectedDepartmentsIds = selectSelectedDepartments(getState());
    const isSelected = selectIsDepartmentIdSelected(departmentId)(getState());

    const updatedSelectedDepartmentsIds = isSelected
      ? selectedDepartmentsIds.filter(
          (selectedDepartmentId: number) => selectedDepartmentId !== departmentId
        )
      : [...selectedDepartmentsIds, departmentId];

    return updatedSelectedDepartmentsIds;
  },
  { serializeError }
);

export const toggleParentDepartmentsOpenByChild = createAsyncThunk(
  `${STORE_NAME}/toggleParentDepartmentsOpenByChild`,
  async ({ currentDepartments }: { currentDepartments: number[] }, { getState, dispatch }) => {
    const allDepartments = selectDepartments(getState());
    const childIds = currentDepartments;

    const findParentFilterToToggle = (
      departments: Department[],
      childId: number,
      parentPath: number[] = []
    ): number[] | null => {
      for (const department of departments) {
        const currentPath = [...parentPath, department.id];

        if (childId === department.id) {
          return currentPath;
        }

        if (department.children) {
          const result = findParentFilterToToggle(department.children, childId, currentPath);
          if (result) {
            return result;
          }
        }
      }
      return null;
    };

    let idsToOpen: number[] = [];

    childIds.forEach((childId) => {
      const path = findParentFilterToToggle(allDepartments, childId);
      if (path) {
        idsToOpen = [...idsToOpen, ...path];
      }
    });

    const uniqueArray = idsToOpen.filter((item, index) => idsToOpen.indexOf(item) === index);

    uniqueArray.forEach((id) => {
      dispatch(toggleDepartmentCollapsedState(id));
    });
  },
  { serializeError }
);

export const selectInitialDepartments = createAsyncThunk(
  `${STORE_NAME}/selectInitialDepartments`,
  (_, { getState, dispatch }) => {
    const selectedDepartmentsIds = selectSelectedDepartmentIds(getState());
    const gotSelectedDepartmentsIds = selectedDepartmentsIds?.length > 0;
    const selectedLocalDepartmentsIds = selectSelectedDepartments(getState());
    const gotSelectedLocalDepartmentsIds = selectedLocalDepartmentsIds?.length > 0;

    let currentDepartments: number[] = [];

    if (gotSelectedDepartmentsIds && !gotSelectedLocalDepartmentsIds) {
      currentDepartments = selectedDepartmentsIds;
      // We only want to dispatch this on the first load
      dispatch(toggleParentDepartmentsOpenByChild({ currentDepartments: selectedDepartmentsIds }));
    } else if (
      JSON.stringify(selectedDepartmentsIds) === JSON.stringify(selectedLocalDepartmentsIds)
    ) {
      currentDepartments = selectedLocalDepartmentsIds;
    } else if (
      JSON.stringify(selectedDepartmentsIds) !== JSON.stringify(selectedLocalDepartmentsIds)
    ) {
      currentDepartments = selectedDepartmentsIds;
    }

    return currentDepartments;
  }
);

export const changeAllDepartments = createAsyncThunk<
  number[],
  { selectAll: boolean; topLevelOnly?: boolean }
>(`${STORE_NAME}/changeAllDepartments`, ({ selectAll, topLevelOnly }, { getState }) => {
  const allDepartments = selectDepartments(getState());

  if (topLevelOnly) {
    // Only add/remove top-level departments; keep any nested selections intact
    const selectedDepartmentsIds = selectSelectedDepartments(getState());
    const topLevelIds = allDepartments.map((department) => department.id);

    if (!selectAll) {
      const topLevelIdSet = new Set(topLevelIds);
      return selectedDepartmentsIds.filter((id) => !topLevelIdSet.has(id));
    }

    const existingIds = new Set(selectedDepartmentsIds);
    const selectableTopLevelIds = allDepartments
      .filter((department) => !department.blocked)
      .map((department) => department.id);
    const newDepartmentIds = selectableTopLevelIds.filter((departmentId) => {
      const command = { type: 'DEPARTMENT' as const, departmentId };
      return !existingIds.has(departmentId) && selectCanApplyFilter(command)(getState());
    });
    return [...selectedDepartmentsIds, ...newDepartmentIds];
  }

  const allDepartmentIds = Department.flattenDepartments(allDepartments).map(
    (department) => department.id
  );

  if (selectAll) {
    const validDepartmentIds = allDepartmentIds.filter((departmentId) => {
      const command = { type: 'DEPARTMENT' as const, departmentId };
      return selectCanApplyFilter(command)(getState());
    });
    return validDepartmentIds;
  } else {
    return [];
  }
});

export const changeDepartmentAndChildren = createAsyncThunk<
  number[],
  { department: Department; select: boolean; directChildrenOnly?: boolean }
>(
  `${STORE_NAME}/changeDepartmentAndChildren`,
  ({ department, select, directChildrenOnly }, { getState }) => {
    const selectedDepartmentsIds = selectSelectedDepartments(getState());
    const allDepartmentIds = directChildrenOnly
      ? Department.getSelectableDirectChildrenIds(department)
      : Department.flattenDepartments([department]).map((dept) => dept.id);

    if (select) {
      const existingIds = new Set(selectedDepartmentsIds);
      const newDepartmentIds = allDepartmentIds.filter((id) => !existingIds.has(id));
      return [...selectedDepartmentsIds, ...newDepartmentIds];
    }

    const departmentIds = new Set(allDepartmentIds);
    return selectedDepartmentsIds.filter((id) => !departmentIds.has(id));
  }
);
